import * as React from 'react';
import {StyleSheet, Pressable, View} from 'react-native';
import Localized from 'src/constants/AppStrings';
import Styles from '../../Styles';
import AVText from '../AVText';
import Util from 'src/Util';
import AccountStore from 'src/stores/AccountStore';
import UnReadlIcon from 'src/components/img/svg/UnReadIcon';
import {getDescriber} from '../descriptor/DescriptorType';
import {getNotificationDescriber} from 'src/components/elements/notificationDescriptor/DescriptorType';
import type {Referral} from 'src/types/Referral';
import type {ScreenProps} from 'src/types/Screen';
import {useSelector} from 'react-redux';
import type {RootState} from 'src/redux/store';

type ReferralItemCanteenProps = ScreenProps & {
  referral: Referral;
  onPress?: () => void;
};

const ReferralItemCanteen: React.FC<ReferralItemCanteenProps> = (props) => {
  const {referral, onPress} = props;
  const accountId = useSelector((state: RootState) => state.account.account.id);

  const renderItemDescription = (isSender: boolean) => {
    const {Labels} = Localized;
    const amount = referral?.amount ?? 0;
    const currency = Util.formatCurrency(
      props,
      amount,
      AccountStore.getCurrency(),
    );

    if (isSender) {
      const receiverName = referral?.receiverName ?? '';
      return Labels.formatString(
        Labels.rewards_description_text_sender,
        receiverName,
        currency,
      );
    } else {
      return Labels.formatString(
        Labels.rewards_description_text_receiver,
        currency,
      );
    }
  };

  const renderItemDescriptionHeaderTitle = () => {
    const {Labels} = Localized;
    const formattedAmount = getFormattedAmountItem(referral?.amount);
    const textItemPrefix = Labels.formatString(
      Labels.referral_invite_friends,
      formattedAmount,
      formattedAmount,
    );
    return `${textItemPrefix}`;
  };

  const getFormattedAmountItem = (amount: number) => {
    const formatted = Util.formatCurrency(
      props,
      amount ?? 0,
      AccountStore.getCurrency(),
    );
    return formatted?.split('.')[0];
  };

  const isSender = referral.sender === accountId;
  const describerprops = {
    referral,
    isSender,
  };

  return (
    <Pressable
      onPress={onPress}
      style={styles.container}
      accessible
      accessibilityHint="Double tap to navigate to the Reward Received detaile screen"
      accessibilityLabel={[
        Localized.Labels.rewards_detail_header_name,
        renderItemDescriptionHeaderTitle(),
        renderItemDescription(isSender),
      ].join('. ')}
      nativeID="Inbox referral Item"
    >
      {getDescriber().referralImageTemplateDescriptor(describerprops)}
      <View style={styles.wrapper}>
        <View style={styles.titleHeaderView}>
          <AVText
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
            numberOfLines={1}
            ellipsizeMode="tail"
            style={[getDescriber().getSnackItemStyleDescriptor().referalTitle]}
            accessible
            testID="qa_NotificationType_txt"
            nativeID="qa_NotificationType_txt"
          >
            {Localized.Labels.rewards_detail_header_name}
          </AVText>

          <AVText
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
            numberOfLines={1}
            ellipsizeMode="tail"
            style={[getDescriber().getSnackItemStyleDescriptor().referralDate]}
            accessible
            testID="qa_MessageDate_txt"
            nativeID="qa_MessageDate_txt"
          >
            {getDescriber().referralItemDateDescriptor(describerprops)}
          </AVText>
        </View>

        <View style={styles.cpUnreadIcon}>
          {!referral.isRead && (
            <UnReadlIcon
              color={getNotificationDescriber().getUnreadMessageIconColor()}
            />
          )}
          <AVText
            style={getDescriber().getSnackItemStyleDescriptor().subject}
            numberOfLines={getDescriber().snackItemNumberDescriptor()}
            accessible
            testID="qa_MessageSubject_txt"
            nativeID="qa_MessageSubject_txt"
          >
            {renderItemDescriptionHeaderTitle()}
          </AVText>
        </View>

        <AVText
          style={[getDescriber().getSnackItemStyleDescriptor().message]}
          numberOfLines={getDescriber().snackItemNumberDescriptor()}
          accessible
          testID="qa_MessageBody_txt"
          nativeID="qa_MessageBody_txt"
        >
          {renderItemDescription(isSender)}
        </AVText>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: Styles.Spacing.m3,
    marginHorizontal: Styles.Spacing.m3,
    paddingBottom: Styles.Spacing.m3,
    flexDirection: 'row',
    minHeight: Styles.Heights.touchTargetHeight2,
  },
  wrapper: {
    flex: 1,
    marginLeft: Styles.Spacing.m3,
  },
  cpUnreadIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  titleHeaderView: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  },
});

export default ReferralItemCanteen;
